import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trophy, Users, Calendar, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDateTime } from "@/lib/api";

export default function Home() {
    const [subleagues, setSubleagues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [adding, setAdding] = useState(false);
    const [newId, setNewId] = useState("");
    const [winterRound, setWinterRound] = useState(17);

    const load = async () => {
        try {
            const subs = await api.get("/subleagues");
            setSubleagues(subs.data);
        } catch (e) {
            toast.error("Kon data niet laden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        const id = parseInt(newId, 10);
        if (!id) {
            toast.error("Voer een geldig subleague-ID in");
            return;
        }
        setAdding(true);
        try {
            await api.post("/subleagues", {
                subleague_id: id,
                winter_break_round: parseInt(winterRound, 10) || 17,
            });
            toast.success("Subleague toegevoegd");
            setNewId("");
            setShowAdd(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Toevoegen mislukt");
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm("Verwijder deze subleague en alle data?")) return;
        try {
            await api.delete(`/subleagues/${id}`);
            toast.success("Verwijderd");
            await load();
        } catch (e) {
            toast.error("Verwijderen mislukt");
        }
    };

    return (
        <div className="space-y-8 fade-up" data-testid="home-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-tiny text-[#00FFAA]">Dashboard</div>
                    <h2 className="text-4xl md:text-5xl font-heading mt-2">
                        Jouw Subleagues
                    </h2>
                    <p className="text-sm text-zinc-400 mt-2 max-w-xl">
                        Volg gele kaarten, rode kaarten, eigen doelpunten en lage rondes per coach.
                        Splits het seizoen voor en na de winterstop.
                    </p>
                </div>
                <button
                    onClick={() => setShowAdd((v) => !v)}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="toggle-add-subleague-btn"
                >
                    <Plus size={16} strokeWidth={3} />
                    Subleague toevoegen
                </button>
            </div>

            {showAdd && (
                <form
                    onSubmit={handleAdd}
                    className="tile p-6 grid md:grid-cols-[1fr_180px_auto] gap-4 items-end fade-up"
                    data-testid="add-subleague-form"
                >
                    <div>
                        <label className="label-tiny block mb-2">Subleague ID</label>
                        <input
                            type="number"
                            className="input-dark"
                            placeholder="Bijv. 27785"
                            value={newId}
                            onChange={(e) => setNewId(e.target.value)}
                            data-testid="subleague-id-input"
                            required
                        />
                        <p className="text-xs text-zinc-500 mt-2">
                            Het nummer in de URL: <span className="text-zinc-300">coachvanhetjaar.nl/subleagues/<b className="text-[#00FFAA]">27785</b>/...</span>
                        </p>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Winterstop ronde</label>
                        <input
                            type="number"
                            className="input-dark"
                            value={winterRound}
                            onChange={(e) => setWinterRound(e.target.value)}
                            data-testid="winter-break-input"
                            min={1}
                            max={38}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={adding}
                        className="btn-primary"
                        data-testid="submit-subleague-btn"
                    >
                        {adding ? "Toevoegen…" : "Toevoegen"}
                    </button>
                </form>
            )}

            {loading ? (
                <div className="grid md:grid-cols-2 gap-5">
                    {[0, 1].map((i) => (
                        <div key={i} className="shimmer h-40 rounded-sm" />
                    ))}
                </div>
            ) : subleagues.length === 0 ? (
                <EmptyState onAdd={() => setShowAdd(true)} />
            ) : (
                <div className="grid md:grid-cols-2 gap-5">
                    {subleagues.map((s) => (
                        <SubleagueCard
                            key={s.subleague_id}
                            sub={s}
                            onDelete={() => handleDelete(s.subleague_id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function SubleagueCard({ sub, onDelete }) {
    return (
        <Link
            to={`/subleague/${sub.subleague_id}`}
            className="tile tile-glow p-6 block group"
            data-testid={`subleague-card-${sub.subleague_id}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="label-tiny text-[#00FFAA]">#{sub.subleague_id}</div>
                    <h3 className="text-2xl md:text-3xl font-heading mt-1.5 truncate group-hover:text-[#00FFAA] transition-colors">
                        {sub.name}
                    </h3>
                </div>
                <Trophy className="text-[#27272a] group-hover:text-[#00FFAA] transition-colors" size={28} />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-6">
                <Stat icon={Users} label="Coaches" value={sub.coach_count} />
                <Stat icon={Calendar} label="Rondes" value={sub.max_round || "–"} />
                <Stat icon={RefreshCw} label="Winterstop" value={`R${sub.winter_break_round}`} />
            </div>
            <div className="stripe-divider my-5" />
            <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-500 uppercase tracking-[0.15em]">
                    Laatste sync: {fmtDateTime(sub.last_refreshed)}
                </span>
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete();
                    }}
                    className="text-zinc-500 hover:text-[#FF3B30] transition-colors text-xs uppercase tracking-[0.15em]"
                    data-testid={`delete-subleague-${sub.subleague_id}`}
                >
                    Verwijder
                </button>
            </div>
        </Link>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div className="bg-[#0A0A0A] border border-[#1f1f23] p-3">
            <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
                <Icon size={11} />
                <span className="text-[0.6rem] uppercase tracking-[0.18em] font-bold">{label}</span>
            </div>
            <div className="font-heading text-xl">{value}</div>
        </div>
    );
}

function EmptyState({ onAdd }) {
    return (
        <div className="tile p-12 text-center" data-testid="empty-state">
            <div className="inline-flex w-14 h-14 items-center justify-center bg-[#0A0A0A] border border-[#27272a] mb-4">
                <Trophy size={24} className="text-[#00FFAA]" />
            </div>
            <h3 className="text-2xl font-heading">Nog geen subleagues</h3>
            <p className="text-zinc-400 mt-2 text-sm max-w-md mx-auto">
                Voeg de eerste subleague toe om te beginnen met het volgen van kaarten, eigen doelpunten en lage rondes.
            </p>
            <button onClick={onAdd} className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="empty-add-btn">
                <Plus size={16} strokeWidth={3} />
                Eerste subleague toevoegen
            </button>
        </div>
    );
}
